import { useEffect, useRef, useState } from 'react';
import { medicineDraftStorageKey, medicineEditorSeed, serializableMedicineDraft, mergeMedicineReviewDraft, refreshMedicineDraftLinks } from './medicineEditorDraft.mjs';

const memory = new Map();
const maxAge = 7 * 24 * 60 * 60 * 1000;
function read(key) {
  if (memory.has(key)) return memory.get(key);
  try { const value = JSON.parse(sessionStorage.getItem(key) || 'null'); if (value && Date.now() - value.at < maxAge) return value; } catch {}
  return null;
}
export function discardMedicineDrafts({ actorId, scopes, language }) {
  for (const scope of new Set(scopes.filter(Boolean))) { const key = medicineDraftStorageKey({ actorId, scope, language }); memory.delete(key); try { sessionStorage.removeItem(key); } catch {} }
}
export function migrateMedicineDrafts({ actorId, fromScope, toScope, languages }) {
  if (!fromScope || !toScope || fromScope === toScope) return;
  for (const language of languages) {
    const from = medicineDraftStorageKey({ actorId, scope: fromScope, language }), to = medicineDraftStorageKey({ actorId, scope: toScope, language });
    const value = read(from); if (!value || read(to)) continue;
    memory.set(to, value); memory.delete(from);
    try { sessionStorage.setItem(to, JSON.stringify({ ...value, ...serializableMedicineDraft(value.state) })); sessionStorage.removeItem(from); } catch {}
  }
}

export default function useMedicineFormDraft({ enabled, scope, language, ready, seed, snapshot, setters, workspace, serverRevision }) {
  const actorId = (() => { try { return JSON.parse(localStorage.getItem('adminLoginInfo'))?._id; } catch { return ''; } })();
  const key = medicineDraftStorageKey({ actorId, scope, language });
  const fallbackKey = medicineDraftStorageKey({ actorId, scope: workspace?.baseDraftScope || scope, language });
  const [initialized, setInitialized] = useState(''), [restored, setRestored] = useState(false), [mediaWarning, setMediaWarning] = useState(false), [stale, setStale] = useState(false);
  const stateRef = useRef(snapshot), settersRef = useRef(setters), seedRef = useRef(seed), saveNonce = useRef({ key: '', id: '' });
  stateRef.current = snapshot; settersRef.current = setters; seedRef.current = seed;
  const initializedRef = useRef('');
  const checkpoint = () => {
    if (!enabled || initializedRef.current !== key) return;
    const value = { at: Date.now(), state: stateRef.current, dirty: Boolean(workspace?.formRef?.current.dirty) };
    if (!value.dirty) return;
    for (const target of new Set([key, fallbackKey])) { memory.set(target, value); try { sessionStorage.setItem(target, JSON.stringify({ ...value, ...serializableMedicineDraft(value.state) })); } catch {} }
  };
  const checkpointRef = useRef(checkpoint); checkpointRef.current = checkpoint;
  useEffect(() => {
    if (!enabled || !ready || initializedRef.current === key) return;
    const cached = read(key), fallback = !cached && seedRef.current && fallbackKey !== key ? read(fallbackKey) : null;
    let initial = cached?.state || (seedRef.current ? medicineEditorSeed(seedRef.current, language) : null);
    if (fallback?.state && initial) {
      initial = mergeMedicineReviewDraft({ seedState: initial, cachedState: fallback.state, selectedFields: workspace?.review?.data.selectedFields, language, generated: seedRef.current.generated !== false });
    }
    // English may have been explicitly saved after this language's draft was
    // cached. Refresh newly assigned relationships without replacing its edits.
    const linked = seedRef.current ? medicineEditorSeed(seedRef.current, language) : stateRef.current;
    if (initial && !initial.englishVariantId && linked.englishVariantId) {
      initial = refreshMedicineDraftLinks(initial, linked);
    }
    if (cached && seedRef.current?.saved && cached.state.revision === undefined) initial = { ...initial, revision: linked.revision, variantArr: linked.variantArr };
    if (initial) for (const [field, value] of Object.entries(initial)) settersRef.current[field]?.(value);
    setRestored(Boolean(cached || fallback)); setMediaWarning(Boolean((cached || fallback)?.mediaNotPersisted));
    const currentRevision = seedRef.current?.content?._revision ?? serverRevision;
    const restoredValue = cached || fallback;
    setStale(Boolean(restoredValue && currentRevision !== undefined && restoredValue.state.revision !== undefined && restoredValue.state.revision !== currentRevision));
    if (cached?.dirty && workspace?.formRef) workspace.formRef.current.dirty = true;
    if (!cached && seedRef.current && !seedRef.current.saved && (seedRef.current.generated !== false || !seedRef.current.existing) && workspace?.review && workspace?.formRef) workspace.formRef.current.dirty = true;
    initializedRef.current = key; setInitialized(key);
  // Seeds are applied once. Polling and reference refreshes never overwrite edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fallbackKey, key, language, ready, workspace?.formRef]);
  useEffect(() => {
    if (!enabled || initialized !== key) return;
    const timer = setTimeout(() => checkpointRef.current(), 200);
    return () => clearTimeout(timer);
  }, [enabled, initialized, key, snapshot]);
  useEffect(() => {
    if (!enabled) return;
    const callback = () => checkpointRef.current();
    if (workspace?.formRef) workspace.formRef.current.checkpoint = callback;
    window.addEventListener('pagehide', callback);
    return () => { callback(); window.removeEventListener('pagehide', callback); if (workspace?.formRef?.current.checkpoint === callback) delete workspace.formRef.current.checkpoint; };
  }, [enabled, key, workspace?.formRef]);
  function saved(result) {
    for (const target of new Set([key, fallbackKey])) { memory.delete(target); try { sessionStorage.removeItem(target); } catch {} }
    if (workspace?.formRef) workspace.formRef.current.dirty = false;
    if (language === 'en' && !workspace?.review && result?.medicineId) migrateMedicineDrafts({ actorId, fromScope: scope, toScope: `family:${result.medicineId}`, languages: ['en','cn','ru','es','sa','pt'] });
    workspace?.refreshReview?.();
  }
  function discard() {
    if (!window.confirm('Discard unsaved changes in this language only? Other language drafts will remain available.')) return;
    discardMedicineDrafts({ actorId, scopes: [scope, workspace?.baseDraftScope], language });
    if (workspace?.formRef) workspace.formRef.current.dirty = false;
    initializedRef.current = '';
    const url = new URL(window.location.href), workspaceId = url.searchParams.get('seoWorkspace');
    if (workspaceId && /\/new$/.test(url.pathname) && !url.searchParams.get('source')) url.searchParams.set('draft', workspaceId);
    for (const field of ['seoWorkspace', 'seoGeneration', 'seoReview']) url.searchParams.delete(field);
    window.location.assign(url.toString());
  }
  function seoReview() {
    if (!workspace?.review?.data || !seedRef.current) return undefined;
    const data = workspace.review.data, signature = JSON.stringify([key, stateRef.current]);
    if (saveNonce.current.key !== signature) saveNonce.current = { key: signature, id: crypto.randomUUID() };
    return { workspaceId: data.workspace.id, generationId: data.generationId, language, sourceRevision: seedRef.current.sourceRevision, requestId: saveNonce.current.id };
  }
  return { initialized: !enabled || initialized === key, restored, stale, mediaWarning, saved, seoReview, discard };
}
